import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import {
  getArtworkOptionLabels,
  getDtfCartItemTitle,
} from "pages/dtf-transfers/by-size/helpers";
import {
  MIN_ORDER_SUBTOTAL,
  meetsMinOrderSubtotal,
} from "pages/dtf-transfers/by-size/data";
import { formatMoney } from "pages/dtf-transfers/checkout/currency";
import {
  TotalsBlock,
  RushSameDayRow,
  RushSameDayLabel,
  TotalLine,
  GrandTotalLine,
  SummaryList,
  SummaryItem,
  SummaryItemThumbWrap,
  QuantityBadge,
  SummaryItemBody,
  SummaryItemTitle,
  SummaryItemDetail,
  SummaryItemThumb,
  SummaryItemMeta,
  SummaryItemFile,
  SummaryItemSize,
  SummaryItemOptions,
  SummaryItemOption,
  SummaryItemPrice,
  MinOrderAlert,
} from "styles/pages/dtf-transfers/checkout";

const CheckoutSummary = ({
  cartItems,
  subtotal,
  itemsSubtotal,
  rushSameDay = false,
  onRushSameDayChange,
  shippingCost = 0,
  taxAmount = 0,
  showTax = false,
  currency = "CAD",
}) => {
  const itemCount = cartItems.reduce((sum, item) => {
    const quantity = item.sizes?.[0]?.quantity ?? 1;
    return sum + quantity;
  }, 0);

  const merchandiseSubtotal =
    itemsSubtotal != null ? itemsSubtotal : subtotal;
  const meetsMinimum = meetsMinOrderSubtotal(merchandiseSubtotal, rushSameDay);
  const total = subtotal + shippingCost + taxAmount;

  return (
    <>
      {!meetsMinimum ? (
        <MinOrderAlert>
          A minimum subtotal of {formatMoney(MIN_ORDER_SUBTOTAL, currency)} is
          required to place an order. Please add more items to continue.
        </MinOrderAlert>
      ) : null}

      <SummaryList>
        {cartItems.map((item, index) => {
          const primarySize = item.sizes?.[0];
          const quantity = primarySize?.quantity ?? 1;
          const sizeLabel = primarySize
            ? `${primarySize.width}" x ${primarySize.height}"`
            : null;
          const optionLabels = getArtworkOptionLabels(item);

          return (
            <SummaryItem key={item.id ?? index}>
              <SummaryItemBody>
                <SummaryItemTitle>{getDtfCartItemTitle(item)}</SummaryItemTitle>
                <SummaryItemDetail>
                  <SummaryItemThumbWrap>
                    <SummaryItemThumb
                      src={item.previewUrl}
                      alt={item.fileName || "Cart item"}
                    />
                    <QuantityBadge>{quantity}</QuantityBadge>
                  </SummaryItemThumbWrap>
                  <SummaryItemMeta>
                    <SummaryItemFile>
                      {item.fileName || "Artwork"}
                    </SummaryItemFile>
                    {sizeLabel ? (
                      <SummaryItemSize>Size: {sizeLabel}</SummaryItemSize>
                    ) : null}
                    {optionLabels.length ? (
                      <SummaryItemOptions>
                        {optionLabels.map((label) => (
                          <SummaryItemOption key={label}>
                            <CheckCircleIcon />
                            {label}
                          </SummaryItemOption>
                        ))}
                      </SummaryItemOptions>
                    ) : null}
                  </SummaryItemMeta>
                </SummaryItemDetail>
              </SummaryItemBody>

              <SummaryItemPrice>
                {formatMoney(item.total, currency)}
              </SummaryItemPrice>
            </SummaryItem>
          );
        })}
      </SummaryList>

      <TotalsBlock>
        <RushSameDayRow $checked={rushSameDay}>
          <RushSameDayLabel>
            <input
              type="checkbox"
              checked={rushSameDay}
              onChange={(event) => onRushSameDayChange?.(event.target.checked)}
            />
            <BoltOutlinedIcon />
            Rush Same Day
          </RushSameDayLabel>
        </RushSameDayRow>

        <TotalLine>
          <span>
            Subtotal · {itemCount} item{itemCount === 1 ? "" : "s"}
            {rushSameDay ? " (Rush)" : ""}
          </span>
          <span>{formatMoney(subtotal, currency)}</span>
        </TotalLine>
        <TotalLine>
          <span>Shipping</span>
          <span>
            {showTax
              ? formatMoney(shippingCost, currency)
              : "Calculated at next step"}
          </span>
        </TotalLine>
        {showTax ? (
          <TotalLine>
            <span>Tax (13%)</span>
            <span>{formatMoney(taxAmount, currency)}</span>
          </TotalLine>
        ) : null}
        <GrandTotalLine>
          <span>Total</span>
          <span>{formatMoney(total, currency)}</span>
        </GrandTotalLine>
      </TotalsBlock>
    </>
  );
};

export default CheckoutSummary;
